import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { hasPerm } from "@/lib/auth";
import { listSuppressions, addSuppression } from "@/lib/api";
import { Loader2, Search, ShieldOff, Plus, RefreshCw, Ban } from "lucide-react";

const REASONS = ["Recipient unsubscribed", "Manual opt-out", "Hard bounce", "Spam complaint"];

const SOURCE_LABELS = {
  unsubscribe_link: "Unsubscribe link",
  manual: "Manual",
  bounce: "Bounce",
  complaint: "Complaint",
};

const REASON_STYLES = {
  "Recipient unsubscribed": "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30",
  "Manual opt-out": "bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/30",
  "Hard bounce": "bg-[#F5B94A]/15 text-[#F5B94A] border-[#F5B94A]/30",
  "Spam complaint": "bg-[#FF6B6B]/15 text-[#FF6B6B] border-[#FF6B6B]/30",
};

const fmtDate = (iso) => {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

export const SuppressionManager = ({ token }) => {
  const canManage = hasPerm("suppressions.manage");
  const [rows, setRows] = useState(null); // null = loading
  const [query, setQuery] = useState("");
  const [reason, setReason] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newReason, setNewReason] = useState("Manual opt-out");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = {};
      if (query.trim()) params.query = query.trim();
      if (reason !== "all") params.reason = reason;
      const d = await listSuppressions(token, params);
      setRows(d.suppressions || []);
    } catch {
      toast.error("Failed to load suppression list");
      setRows([]);
    } finally {
      setRefreshing(false);
    }
  }, [token, query, reason]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce search/filter
    return () => clearTimeout(t);
  }, [load]);

  const submitAdd = async () => {
    if (!newEmail.trim()) { toast.error("Enter an email address"); return; }
    setSaving(true);
    try {
      await addSuppression(token, { email: newEmail.trim(), reason: newReason });
      toast.success("Address suppressed globally.");
      setAddOpen(false);
      setNewEmail("");
      setNewReason("Manual opt-out");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add suppression");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="suppression-manager">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <ShieldOff size={18} className="text-[#F5B94A]" /> Email Suppression
          </h2>
          <p className="text-white/50 text-sm">
            Global opt-out list. Suppressed addresses are blocked from all Rendeavored marketing
            emails across every campaign. Records are retained for audit.
          </p>
        </div>
        {canManage && (
          <Button
            onClick={() => setAddOpen(true)}
            className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90"
            data-testid="suppression-add-button"
          >
            <Plus size={15} className="mr-1.5" /> Suppress Marketing Email
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email or campaign..."
            data-testid="suppression-search"
            className="h-10 pl-9 bg-[#0A0F1E] border-white/10 text-white placeholder:text-white/35 focus-visible:ring-[#1E6BFF]/50"
          />
        </div>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger className="h-10 w-52 bg-[#0A0F1E] border-white/10 text-white" data-testid="suppression-reason-filter">
            <SelectValue placeholder="All reasons" />
          </SelectTrigger>
          <SelectContent className="bg-[#0E1424] border-white/10 text-white">
            <SelectItem value="all" className="focus:bg-white/10 focus:text-white">All reasons</SelectItem>
            {REASONS.map((r) => (
              <SelectItem key={r} value={r} className="focus:bg-white/10 focus:text-white">{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          onClick={load}
          disabled={refreshing}
          className="text-white/70 hover:text-white hover:bg-white/5 h-10"
          data-testid="suppression-refresh"
        >
          <RefreshCw size={15} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/10 bg-[#0A0F1E] overflow-hidden">
        {rows === null ? (
          <div className="flex items-center justify-center py-16 text-white/50">
            <Loader2 className="animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-white/45" data-testid="suppression-empty">
            No suppressed addresses{query || reason !== "all" ? " match your filters" : " yet"}.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/50">Email</TableHead>
                <TableHead className="text-white/50">Prospect</TableHead>
                <TableHead className="text-white/50">Reason</TableHead>
                <TableHead className="text-white/50">Source</TableHead>
                <TableHead className="text-white/50">Campaign</TableHead>
                <TableHead className="text-white/50">Suppressed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id || s.email} className="border-white/10 hover:bg-white/5" data-testid="suppression-row">
                  <TableCell className="text-white/90 font-medium break-all" data-testid="suppression-email">{s.email}</TableCell>
                  <TableCell className="text-white/60">{s.prospect_name || "\u2014"}</TableCell>
                  <TableCell>
                    <Badge className={`border ${REASON_STYLES[s.reason] || "bg-white/5 text-white/60 border-white/15"}`}>
                      {s.reason}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-white/60">{SOURCE_LABELS[s.source] || s.source || "\u2014"}</TableCell>
                  <TableCell className="text-white/60">{s.campaign || "\u2014"}</TableCell>
                  <TableCell className="text-white/60 whitespace-nowrap">{fmtDate(s.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add / manual suppression dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md bg-[#0E1424] border-white/10 text-white" data-testid="suppression-add-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <Ban size={18} className="text-[#F5B94A]" /> Suppress Marketing Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-1">
            <p className="text-white/55 text-sm">
              Add an address to the global suppression list (e.g. someone replied &ldquo;remove me&rdquo;).
              They&rsquo;ll be blocked from all future marketing emails across every campaign.
            </p>
            <div>
              <div className="text-white/70 text-xs mb-1">Email address</div>
              <Input
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="person@example.com"
                data-testid="suppression-add-email"
                className="h-10 bg-[#0A0F1E] border-white/10 text-white placeholder:text-white/35 focus-visible:ring-[#1E6BFF]/50"
              />
            </div>
            <div>
              <div className="text-white/70 text-xs mb-1">Reason</div>
              <Select value={newReason} onValueChange={setNewReason}>
                <SelectTrigger className="h-10 bg-[#0A0F1E] border-white/10 text-white" data-testid="suppression-add-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                  {REASONS.map((r) => (
                    <SelectItem key={r} value={r} className="focus:bg-white/10 focus:text-white">{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setAddOpen(false)} className="text-white/70 hover:text-white hover:bg-white/5" data-testid="suppression-add-cancel">
              Cancel
            </Button>
            <Button onClick={submitAdd} disabled={saving} className="bg-[#F5B94A] hover:bg-[#F5B94A]/90 text-[#0A0F1E]" data-testid="suppression-add-confirm">
              {saving ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Ban size={15} className="mr-1" />} Suppress
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppressionManager;
